import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend} from 'recharts';
import {Link, useNavigate} from "react-router-dom";
import axios from "axios";
import swal from 'sweetalert';
import "../../assets/css/pages/dashboard.css";
import "../../assets/css/pages/dashboard-charts.css";
import DateInput from "../../Components/Inputs/DateInput";
import {useForm} from "react-hook-form";
import dayjs from "dayjs";
import {DATE_FORMAT} from "../../Constants/Constants";
import {downloadBlob} from "../../Components/Helpers/Utils";
import {confirmAlert} from "../../Components/Alerts/BasicAlert";

const endDate = dayjs();
const startDate = endDate.subtract(30, 'day');
export default function () {
  const user = useSelector(state => state.user);
  const isAdminType = user?.isAdminType;
  const [dashboardData, setDashboardData] = useState([]);
  const [deleteIndexForTrigger, setDeleteIndexForTrigger] = useState(0);
  const [errors, setErrors] = useState({})
  const [data, setData] = useState([]);

  const {register, handleSubmit, watch, getValues} = useForm({
    defaultValues: {
      'ordersQuantityByStatus': {date_from: startDate.format(DATE_FORMAT), date_to: endDate.format(DATE_FORMAT)},
      'ordersByPaymentProvider': {date_from: startDate.format(DATE_FORMAT), date_to: endDate.format(DATE_FORMAT)},
      'salesStatistics': {date_from: startDate.format(DATE_FORMAT), date_to: endDate.format(DATE_FORMAT)},
      'totalProfit': {date_from: startDate.format(DATE_FORMAT), date_to: endDate.format(DATE_FORMAT)},
    }
  });

  const [filterParams, setFilterParams] = useState({
    chartIndex: 1,
  });

  const daysChartIndex = 1;
  const weeksChartIndex = 2;
  const monthsChartIndex = 3;

  useEffect(() => {
    let filters = {...filterParams, ...getValues()}
    axios.post("dashboard", filters).then(res => {
      if (res.data.status === 'success') {
        setDashboardData(res?.data?.data);
      }
    }).catch(function (error) {
      if (error.response) {
        setErrors(error.response.data.errors || {});
      }
    });
  }, [...Object.values({...filterParams,
      ...watch('ordersQuantityByStatus'),
      ...watch('totalProfit'),
      ...watch('ordersByPaymentProvider')
  }
  ), deleteIndexForTrigger], );

  const downloadOrdersQuantityByStatusReport = () => {
    let params = getValues('ordersQuantityByStatus')
    axios.get(`/exports/orders-quantity-by-status`, {
      params: params,
      responseType: 'blob',
    }).then(res => {
      downloadBlob(res.data, 'orders-' + params['date_from'] + '_' + params['date_to'] + '.xlsx');
    });
  }

  const downloadSalesStatisticsReport = () => {
    let params = getValues('salesStatistics')
    axios.get(`/exports/sales-statistics`, {
      params: params,
      responseType: 'blob',
    }).then(res => {
      downloadBlob(res.data, 'sales-statistics-' + params['date_from'] + '_' + params['date_to'] + '.xlsx');
    });
  }

  const deleteReview = (id) => {
    confirmAlert('Do you want to delete feedback?', () => {
      axios.delete('feedbacks/' + id).then(res => {
        setDeleteIndexForTrigger(id);
      });
    });
  }

  useEffect(() => {
    axios.get('/orders/filters').then(res => {
      setData(res.data);
    });
  }, []);

  return (
    <>
      <div>
        <div id="dashboard" className="dashboard">
          <div className="order-total-values">
            <h2 className="text-center text-muted mt-10 mb-15 ls-1">Orders price SUM</h2>
            <div className="row justify-content-center">
              <div className="col-md-4 pb-8 px-10">
                <Link to={"/orders"}
                      state={{
                        'date_from': endDate.startOf('day').format(DATE_FORMAT),
                        'date_to': endDate.format(DATE_FORMAT),
                        'payment_status': data?.paymentStatuses?.find(status => status.name === 'Paid')?.id
                      }}
                >
                  <div className="bg-light rounded text-center px-7 pt-5 pb-3">
                    <div className="icon my-5">
                      <i className="fas fa-calendar-day fs-1 text-muted"></i>
                    </div>
                    <div className="title my-5 text-muted ls-1 fs-6">Today</div>
                    <div className="count my-5 text-muted ls-1 fs-4">
                      {dashboardData && dashboardData?.orders_today_money}
                      {
                        dashboardData && (
                          <>
                            <div className="count my-5 ls-1 fs-4">
                              Order total: {dashboardData?.orders_today?.money}
                            </div>
                            <div className="count my-5 ls-1 fs-4">
                              Count: {dashboardData?.orders_today?.count}
                            </div>
                          </>
                        )
                      }
                    </div>
                  </div>
                </Link>
              </div>
              <div className="col-md-4 pb-8 px-10">
                <Link to={"/orders"}
                      state={{
                        'date_from': endDate.startOf('month').format(DATE_FORMAT),
                        'date_to': endDate.format(DATE_FORMAT),
                        'payment_status': data?.paymentStatuses?.find(status => status.name === 'Paid')?.id
                      }}
                >
                  <div className="bg-light rounded text-center px-7 pt-5 pb-3">
                    <div className="icon my-5">
                      <i className="far fa-calendar fs-1 text-info"></i>
                    </div>
                    <div className="title my-5 text-info ls-1 fs-6">This Month</div>
                    <div className="count my-5 text-info ls-1 fs-4">
                      {
                        dashboardData && (
                          <>
                            <div className="count my-5 ls-1 fs-4">
                              Order total: {dashboardData?.orders_month?.money}
                            </div>
                            <div className="count my-5 ls-1 fs-4">
                              Count: {dashboardData?.orders_month?.count}
                            </div>
                          </>
                        )
                      }
                    </div>
                  </div>
                </Link>

              </div>
              <div className="col-md-4 pb-8 px-10">
                <Link to={"/orders"}
                      state={{
                        'date_from': endDate.startOf('year').format(DATE_FORMAT),
                        'date_to': endDate.format(DATE_FORMAT),
                        'payment_status': data?.paymentStatuses?.find(status => status.name === 'Paid')?.id
                      }}
                >
                  <div className="bg-light rounded text-center px-7 pt-5 pb-3">
                    <div className="icon my-5">
                      <i className="fas fa-layer-group fs-1 text-primary"></i>
                    </div>
                    <div className="title my-5 text-primary ls-1 fs-6">This Year</div>
                    <div className="count my-5 text-primary ls-1 fs-4">
                      {
                        dashboardData && (
                          <>
                            <div className="count my-5 ls-1 fs-4">
                              Order total: {dashboardData?.orders_year?.money}
                            </div>
                            <div className="count my-5 ls-1 fs-4">
                              Count: {dashboardData?.orders_year?.count}
                            </div>
                          </>
                        )
                      }
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          <div className="col-md-6 bg-light rounded text-center px-7 pt-5 pb-3">
            <h3 className="mb-0 pt-3 text-muted ls-1 d-inline-block">Total Profit</h3>
            <div className="count my-5 text-muted ls-1 fs-4">
              <div className="row">
                <div className="col-xl-12 mb-5 mb-xl-0">
                  <div className="">
                    {/* shadow */}
                    <div className="px-5 pt-5 py-3 border-0">
                      <div className="row align-items-center">
                        <DateInput
                            label="Start Date"
                            name="totalProfit.date_from"
                            parentClasses="col-md-6 mt-4"
                            register={register}
                            errorMessage={errors?.['totalProfit.date_from']}
                        />
                        <DateInput
                            label="End date"
                            name="totalProfit.date_to"
                            parentClasses="col-md-6 mt-4"
                            register={register}
                            errorMessage={errors?.['totalProfit.date_to']}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {
                <>
                  <div className="count my-5 ls-1 fs-4">
                    Profit: {dashboardData && dashboardData?.total_profit}
                  </div>
                </>
              }
            </div>
          </div>

          <div className="order-total-values">
            <h2 className="text-center text-muted mt-10 mb-15 ls-1">Orders By Payment Method</h2>
            <div className="row justify-content-center">
              {
                dashboardData && dashboardData?.paid_orders_by_payment_method?.map((item, key) => {
                  return (
                    <div className="col-md-6 pb-8 px-10" key={key}>
                      <div className="bg-light rounded text-center px-7 pt-5 pb-3">
                        <div className="icon my-5">
                          <i className="fa fa-credit-card fs-1 text-muted"></i>
                        </div>
                        <div className="title my-5 text-muted ls-1 fs-6">
                          {dashboardData.payment_methods?.find(method => method.id === item.payment_method)?.name}
                        </div>
                        <div className="count my-5 text-muted ls-1 fs-4">
                          <>
                            <div className="count my-5 ls-1 fs-4">
                              Orders: {item.total}
                            </div>
                            <div className="count my-5 ls-1 fs-4">
                              Sales: {item.total_price}
                            </div>
                          </>
                        </div>
                      </div>
                    </div>
                  )
                })
              }
            </div>
          </div>

          {
            isAdminType && (
              <div className="bg-main-color rounded mt-10"  >
                <div className="row">
                  <div className="col-xl-12 mb-5 mb-xl-0">
                    <div className="">
                      {/* shadow */}
                      <div className="px-5 pt-5 py-3 border-0">
                        <div className="row align-items-center">
                          <h3 className="mb-0 pt-3 text-muted ls-1 d-inline-block">
                            Orders by payment provider
                          </h3>
                          <DateInput
                            label="Start Date"
                            name="ordersByPaymentProvider.date_from"
                            parentClasses="col-md-6 mt-4"
                            register={register}
                            errorMessage={errors?.['ordersByPaymentProvider.date_from']}
                          />
                          <DateInput
                            label="End date"
                            name="ordersByPaymentProvider.date_to"
                            parentClasses="col-md-6 mt-4"
                            register={register}
                            errorMessage={errors?.['ordersByPaymentProvider.date_to']}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row px-5">

                  {

                    dashboardData && dashboardData?.paid_orders_by_payment_provider?.map((item, key) => {
                      return (
                        <div className="col-md-6 pb-8 px-10" key={key}>
                          <div className="bg-white rounded text-center px-7 pt-5 pb-3">
                            <div className="icon my-5">
                              <i className="fas fa-university fs-1 text-muted"></i>
                            </div>
                            <div className="title my-5 text-muted ls-1 fs-6">
                              {dashboardData.payment_providers?.find(provider => provider.id === item.payment_provider)?.name}
                            </div>
                            <div className="count my-5 text-muted ls-1 fs-4">
                              <>
                                <div className="count my-5 ls-1 fs-4">
                                  Orders: {item.total}
                                </div>
                                <div className="count my-5 ls-1 fs-4">

                                  Sales: {item.total_price}
                                </div>
                              </>
                            </div>
                          </div>
                        </div>
                      )
                    })
                  }
                </div>
              </div>
            )
          }

          <div className="order-status-count bg-main-color rounded mt-10">
            <div className="row">
              <div className="col-xl-12 mb-5 mb-xl-0">
                <div className="">
                  {/* shadow */}
                  <div className="px-5 pt-5 py-3 border-0">
                    <div className="row align-items-center">
                      <h3 className="mb-0 pt-3 text-muted ls-1 d-inline-block">
                        Orders quantity by status
                      </h3>
                      <div className="text-center">
                        {dashboardData?.line_diagram_error &&
                          <>
                            <span className="dashboard-diagrams-error-message"><br/>
                              {dashboardData?.line_diagram_error}
                            </span>
                          </>
                        }
                      </div>
                      <DateInput
                        label="Start Date"
                        name="ordersQuantityByStatus.date_from"
                        parentClasses="col-md-5 mt-4"
                        register={register}
                        errorMessage={errors?.['ordersQuantityByStatus.date_from']}
                      />
                      <DateInput
                        label="End date"
                        name="ordersQuantityByStatus.date_to"
                        parentClasses="col-md-5 mt-4"
                        register={register}
                        errorMessage={errors?.['ordersQuantityByStatus.date_to']}
                      />
                      <div className='col-md-2 pt-2'>
                        <button className="btn btn-primary py-4 d-block w-100 ls-1" type="button"
                                onClick={() => downloadOrdersQuantityByStatusReport()}>Export
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {dashboardData &&
              <div className="row px-5">
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"}
                        state={{
                          ...watch('ordersQuantityByStatus'),
                          'status': data?.statuses?.find(item => item.name === 'Pending')?.id
                        }}
                  >
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon bg-secondary mr-3 px-5 pt-4">
                        <i className="fas fa-question"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Pending</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_pending_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"}
                        state={{
                          ...watch('ordersQuantityByStatus'),
                          'status': data?.statuses?.find(item => item.name === 'Active')?.id
                        }}
                  >
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon play bg-info mr-3 px-6 pt-3">
                        <i className="fas fa-caret-right"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Active</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_active_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"}
                        state={{
                          ...watch('ordersQuantityByStatus'),
                          'status': data?.statuses?.find(item => item.name === 'Ready For Shipment')?.id
                        }}
                  >
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon total bg-success mr-3 px-4 pt-4">
                        <i className="fas fa-truck"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Ready For Shipment</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_ready_for_shipment_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"}
                        state={{
                          ...watch('ordersQuantityByStatus'),
                          'status': data?.statuses?.find(item => item.name === 'Shipped')?.id
                        }}
                  >
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon total bg-primary mr-3 px-4 pt-4">
                        <i className="fas fa-ellipsis-v"></i>
                        <i className="fas fa-ellipsis-v"></i>
                        <i className="fas fa-ellipsis-v"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Shipped</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_shipped_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"}
                        state={{
                          ...watch('ordersQuantityByStatus'),
                          'status': data?.statuses?.find(item => item.name === 'Delivered')?.id
                        }}
                  >
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon bg-success mr-3 pl-3 pt-4">
                        <i className="fas fa-check"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Delivered</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_delivered_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"}
                        state={{
                          ...watch('ordersQuantityByStatus'),
                          'status': data?.statuses?.find(item => item.name === 'Refund')?.id
                        }}
                  >
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon bg-warning mr-3 px-5 pt-4">
                        <i className="fas fa-comment-dollar"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Refunded</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_refund_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"}
                        state={{
                          ...watch('ordersQuantityByStatus'),
                          'status': data?.statuses?.find(item => item.name === 'Canceled')?.id
                        }}
                  >
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon canceled bg-danger mr-3 px-5 pt-4">
                        <i className="fas fa-times"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Canceled</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_canceled_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
                <div className="col-xxl-4 col-sm-6 py-5 col-lg-4">
                  <Link to={"/orders"} state={{...watch('ordersQuantityByStatus')}}>
                    <div className="px-7 py-7 bg-white rounded">
                      <div className="icon total bg-primary mr-3 px-4 pt-4">
                        <i className="fas fa-ellipsis-v"></i>
                        <i className="fas fa-ellipsis-v"></i>
                        <i className="fas fa-ellipsis-v"></i>
                      </div>
                      <div className="title-count">
                        <div className="title text-muted fs-5">Total</div>
                        <div
                          className="count text-muted fs-4 mt-1">{dashboardData && dashboardData?.orders_month_count}</div>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            }
          </div>

          <div className="sales-chart bg-main-color rounded mt-10">
            <div className="row">
              <div className="col-xl-12 mb-5 mb-xl-0">
                <div className="border-bottom">
                  {/* shadow */}
                  <div className="px-5 py-3 border-0">
                    <div className="row align-items-center">
                      <div>
                        <h3 className="mb-0 pt-3 text-muted ls-1 d-inline-block">
                          Sales statistics chart
                          {filterParams.chartIndex == daysChartIndex && " (Days)"}
                          {filterParams.chartIndex == weeksChartIndex && " (Weeks)"}
                          {filterParams.chartIndex == monthsChartIndex && " (Months)"}
                        </h3>
                        {dashboardData?.line_diagram_error &&
                          <>
                  <span className="dashboard-diagrams-error-message"><br/>
                    {dashboardData?.line_diagram_error}
                  </span>
                          </>
                        }

                        <div className="float-right chart-change">
                          <select name="" id="" className="form-control form-control-lg form-control-solid"
                                  onChange={(e) => setFilterParams({
                                    ...filterParams,
                                    chartIndex: parseInt(e.target.value)
                                  })}
                                  title="Change Chart"
                          >
                            <option key={1} value="1">Last 10 Days</option>
                            <option key={2} value="2">Last 10 Weeks</option>
                            <option key={3} value="3">Last 10 Months</option>
                          </select>
                        </div>
                      </div>
                      <DateInput
                        label="Start Date"
                        name="salesStatistics.date_from"
                        parentClasses="col-md-5 mt-4"
                        register={register}
                        errorMessage={errors?.['salesStatistics.date_from']}
                      />
                      <DateInput
                        label="End date"
                        name="salesStatistics.date_to"
                        parentClasses="col-md-5 mt-4"
                        register={register}
                        errorMessage={errors?.['salesStatistics.date_to']}
                      />
                      <div className='col-md-2 pt-2'>
                        <button className="btn btn-primary py-4 d-block w-100 ls-1" type="button"
                                onClick={() => downloadSalesStatisticsReport()}>Export
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {dashboardData &&
              <div className="text-center py-5">
                <div className="d-inline-block">
                  <BarChart
                    width={900}
                    height={400}
                    data={dashboardData?.ten_orders_sum}
                  >
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey='orders_created_at'/>
                    <YAxis/>
                    <Tooltip/>
                    <Legend/>
                    <Bar dataKey="price_sum" fill="#4fc9da"/>
                  </BarChart>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </>
  );
}